<template>
  <div class="hero-section-root">
    <div
      v-if="isHubspotIntegrationEnabled && hubspotHeroState !== 'empty'"
      class="hero-section hero-section__hubspot-container"
    >
      <HubspotCard
        :hubspotId="HUBSPOT.ID"
        :imgSrc="HUBSPOT.IMG"
        @state-change="setHubspotHeroState"
      />
    </div>
    <div v-else-if="!isHubspotIntegrationEnabled" class="hero-section">
      <ShopDefaultHeroSection v-if="heroSectionToShow === 'default'" />
      <PremiumMonthlyPromoSection v-if="heroSectionToShow === 'premium-monthly'" />
      <EDAPremiumPreSaleSection v-if="heroSectionToShow === 'eeda-presale'" />
      <BooGoHeroSection v-if="heroSectionToShow === 'boogo'" />
      <BlackFridayHeroSection v-if="heroSectionToShow === 'black-friday'" />
      <LeapYearHeroSection v-if="heroSectionToShow === 'leap-year-2024'" />
      <TCSPromoHeroSection v-if="heroSectionToShow === 'tcs-promo'" />
      <FlashSaleHeroSection v-if="heroSectionToShow === 'flash-sale'" />
      <PremiumPlusPromoHeroSection v-if="heroSectionToShow === 'premium-plus'" />
      <SpringHeroSection v-if="heroSectionToShow === 'spring'" />
      <MaySubscriptionHeroSection v-if="heroSectionToShow === 'may-sale'" />
      <SummerSavingsHeroSection v-if="heroSectionToShow === 'summer-savings'" />
      <MayFlashSaleHeroSection v-if="heroSectionToShow === 'may-flash-sale'" />
      <JulySaleHeroSection v-if="heroSectionToShow === 'july-sale'" />
      <EmaptAndEcthpPreSaleHeroSection v-if="heroSectionToShow === 'emapt-and-ecthp-presale'" />
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { mapState, mapGetters } from 'vuex'
import { routesRules } from '@/helpers/mixins/routesRules'
import HubspotCard from '@/components/hero-sections/HubspotCard.vue'
import CONSTANTS from '@/constants'

const heroComponentLoaders = {
  default: () => import('@/components/hero-sections/ShopDefaultHeroSection.vue'),
  'premium-monthly': () => import('@/components/hero-sections/PremiumMonthlyPromoSection.vue'),
  'eeda-presale': () => import('@/components/hero-sections/EDAPremiumPreSaleSection.vue'),
  boogo: () => import('@/components/hero-sections/BooGoHeroSection.vue'),
  'black-friday': () => import('@/components/hero-sections/BlackFridayHeroSection.vue'),
  'leap-year-2024': () => import('@/components/hero-sections/LeapYearHeroSection.vue'),
  'tcs-promo': () => import('@/components/hero-sections/TCSPromoHeroSection.vue'),
  'flash-sale': () => import('@/components/hero-sections/FlashSaleHeroSection.vue'),
  spring: () => import('@/components/hero-sections/SpringHeroSection.vue'),
  'premium-plus': () => import('@/components/hero-sections/PremiumPlusPromoHeroSection.vue'),
  'may-sale': () => import('@/components/hero-sections/MaySubscriptionHeroSection.vue'),
  'may-flash-sale': () => import('@/components/hero-sections/MayFlashSaleHeroSection.vue'),
  'summer-savings': () => import('@/components/hero-sections/SummerSavingsHeroSection.vue'),
  'july-sale': () => import('@/components/hero-sections/JulySaleHeroSection.vue'),
}

const heroImagePreloaders = {
  boogo: () => import('@/assets/images/hero-section/boogo/boogo_pumpkin-light.png'),
  'leap-year-2024': () => import('@/assets/images/hero-section/neon-circle-logo.png'),
  'tcs-promo': () => import('@/assets/images/hero-section/tcs-promo/tcs-promo.png'),
  'flash-sale': () => import('@/assets/images/hero-section/flash-sale/flash-sale-logo.png'),
  spring: () => import('@/assets/images/hero-section/spring/spring-logo.png'),
  'may-sale': () => import('@/assets/images/hero-section/may-subscription/ms-logo.png'),
  'may-flash-sale': () => import('@/assets/images/hero-section/may-flash-sale/mfs-logo.png'),
  'summer-savings': () =>
    import('@/assets/images/hero-section/summer-savings/summer-savings-logo.png'),
  'july-sale': () => import('@/assets/images/hero-section/july-sale/july-sale-logo.png'),
  'black-friday': () => import('@/assets/images/hero-section/black-friday/bl-logo.svg'),
  'premium-plus': () => import('@/assets/images/hero-section/premium-plus/pp-logo.svg'),
  'eeda-presale': () => import('@/assets/icons/certs/eeda-badge-white.webp'),
  hubspot: () => Promise.resolve(CONSTANTS.HUBSPOT.HERO_BANNER.IMG),
}

export default {
  name: 'HeroSection',
  emits: ['hero-section-to-show', 'hubspot-hero-visibility-change'],
  mixins: [routesRules],
  components: {
    HubspotCard,
    ShopDefaultHeroSection: defineAsyncComponent(heroComponentLoaders.default),
    PremiumMonthlyPromoSection: defineAsyncComponent(heroComponentLoaders['premium-monthly']),
    EDAPremiumPreSaleSection: defineAsyncComponent(heroComponentLoaders['eeda-presale']),
    BooGoHeroSection: defineAsyncComponent(heroComponentLoaders.boogo),
    BlackFridayHeroSection: defineAsyncComponent(heroComponentLoaders['black-friday']),
    LeapYearHeroSection: defineAsyncComponent(heroComponentLoaders['leap-year-2024']),
    TCSPromoHeroSection: defineAsyncComponent(heroComponentLoaders['tcs-promo']),
    FlashSaleHeroSection: defineAsyncComponent(heroComponentLoaders['flash-sale']),
    PremiumPlusPromoHeroSection: defineAsyncComponent(heroComponentLoaders['premium-plus']),
    SpringHeroSection: defineAsyncComponent(heroComponentLoaders.spring),
    MaySubscriptionHeroSection: defineAsyncComponent(heroComponentLoaders['may-sale']),
    MayFlashSaleHeroSection: defineAsyncComponent(heroComponentLoaders['may-flash-sale']),
    SummerSavingsHeroSection: defineAsyncComponent(heroComponentLoaders['summer-savings']),
    JulySaleHeroSection: defineAsyncComponent(heroComponentLoaders['july-sale']),
  },
  data() {
    return {
      heroSectionToShow: 'default',
      preloadedHeroAssets: {},
      HUBSPOT: CONSTANTS.HUBSPOT.HERO_BANNER,
      hubspotHeroState: 'loading',
    }
  },
  computed: {
    ...mapState({
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
      showGenericBackground: state => state.app.showGenericBackground,
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
      isEDAPremiumPreSaleActive: state => state.app.isEDAPremiumPreSaleActive,
      isAuthenticated: state => state.auth.isAuthenticated,
      userHasSubscription: state => state.subscriptions.userHasSubscription,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
    }),
    ...mapGetters({
      isBooGoPromoActive: 'app/getIsBooGoPromoActive',
      isLeapYearPromoActive: 'app/isLeapYearPromoActive',
      isBusinessUser: 'business/isBusinessUser',
      isTCSPromoEnabled: 'featureFlags/isTCSPromoEnabled',
      isFlashSaleActive: 'featureFlags/isFlashSaleActive',
      premiumPlusEnabled: 'featureFlags/premiumPlusEnabled',
      isPremiumPlusEnabledForUser: 'subscriptions/isPremiumPlusEnabledForUser',
      userHasSkillDive: 'subscriptions/userHasSkillDive',
      isSpringPromoEnabled: 'featureFlags/isSpringPromoEnabled',
      isMaySaleEnabled: 'featureFlags/isMaySaleEnabled',
      isMayFlashSaleEnabled: 'featureFlags/isMayFlashSaleEnabled',
      isSummerSavingsEnabled: 'featureFlags/isSummerSavingsEnabled',
      isJulySaleEnabled: 'featureFlags/isJulySaleEnabled',
      isHubspotIntegrationEnabled: 'featureFlags/isHubspotIntegrationEnabled',
    }),
    heroSectionToggler() {
      return [
        {
          name: 'eeda-presale',
          validation: [
            !this.isOnePlanAvailable,
            this.isEDAPremiumPreSaleActive,
            this.isShopPage || this.isEDAPremiumPage,
          ],
        },
        {
          name: 'premium-monthly',
          validation: [
            !this.isOnePlanAvailable,
            this.isPremiumMonthlyPromoActive,
            this.isShopPage || this.isPremiumMonthlyPromoPage,
          ],
        },
        {
          name: 'boogo',
          validation: [
            [
              !this.isAuthenticated,
              !this.isOnePlanAvailable,
              this.isBooGoPromoActive,
              this.isShopPage || this.isBooGoPage,
            ],
            [
              this.isAuthenticated,
              !this.isOnePlanAvailable,
              this.isBooGoPromoActive,
              !(this.usersCurrentSubscriptions.length > 0),
              this.userHasSkillDive,
              this.isShopPage || this.isBooGoPage,
            ],
          ],
        },
        {
          name: 'leap-year-2024',
          validation: [
            !this.isOnePlanAvailable,
            this.isLeapYearPromoActive,
            this.isShopPage || this.isLeapYearPromoPage,
          ],
        },
        {
          name: 'tcs-promo',
          validation: [!this.isOnePlanAvailable, this.isTCSPromoEnabled, this.isShopPage],
        },
        {
          name: 'flash-sale',
          validation: [
            !this.isSummerSavingsEnabled,
            !this.isOnePlanAvailable,
            this.isFlashSaleActive,
            this.isShopPage,
          ],
        },
        {
          name: 'spring',
          validation: [!this.isOnePlanAvailable, this.isSpringPromoEnabled, this.isShopPage],
        },
        {
          name: 'premium-plus',
          validation: [!this.isOnePlanAvailable, this.isPremiumPlusEnabledForUser, this.isShopPage],
        },
        {
          name: 'may-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isMaySaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'may-flash-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isMayFlashSaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'summer-savings',
          validation: [!this.isOnePlanAvailable, this.isSummerSavingsEnabled, this.isShopPage],
        },
        {
          name: 'july-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isJulySaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'default',
          validation: [this.showGenericBackground],
        },
      ]
    },
    watchVariables() {
      return {
        isTCSPromoEnabled: this.isTCSPromoEnabled,
        isAuthenticated: this.isAuthenticated,
        showGenericBackground: this.showGenericBackground,
        isFlashSaleActive: this.isFlashSaleActive,
        isSpringPromoEnabled: this.isSpringPromoEnabled,
        isPremiumPlusEnabledForUser: this.isPremiumPlusEnabledForUser,
        isMaySaleEnabled: this.isMaySaleEnabled,
        isMayFlashSaleEnabled: this.isMayFlashSaleEnabled,
        isSummerSavingsEnabled: this.isSummerSavingsEnabled,
        isJulySaleEnabled: this.isJulySaleEnabled,
      }
    },
  },
  methods: {
    setHubspotHeroState(state) {
      if (this.hubspotHeroState === state) return
      this.hubspotHeroState = state
      this.$emit('hubspot-hero-visibility-change', { state })
    },
    setHeroSectionToShow() {
      const heroHeaderValue = this.heroSectionToggler.find(heroHeader => {
        if (heroHeader.validation[0].length > 1) {
          const validations = heroHeader.validation.find(validationsBlock =>
            validationsBlock.every(v => v === true)
          )
          return !!validations
        }
        return heroHeader.validation.every(v => v === true)
      })
      const heroHeaderOutput = heroHeaderValue ? heroHeaderValue.name : 'default'
      return !this.isShopPage ? '' : heroHeaderOutput
    },
    updateHeroSection() {
      this.heroSectionToShow = this.setHeroSectionToShow()
      this.$emit('hero-section-to-show', this.heroSectionToShow)
      const heroKey = this.isHubspotIntegrationEnabled ? 'hubspot' : this.heroSectionToShow
      this.warmHeroComponent(heroKey)
      this.preloadHeroAsset(heroKey)
    },
    warmHeroComponent(heroKey) {
      const loader = heroComponentLoaders[heroKey]
      if (loader) {
        loader()
      }
    },
    async preloadHeroAsset(heroKey) {
      if (typeof document === 'undefined' || !heroKey || this.preloadedHeroAssets[heroKey]) return
      const loader = heroImagePreloaders[heroKey]
      if (!loader) return
      try {
        const assetModule = await loader()
        const href = assetModule?.default || assetModule
        if (typeof href !== 'string' || !href) return
        const existingPreload = document.head.querySelector(
          `link[rel="preload"][as="image"][href="${href}"]`
        )
        if (existingPreload) {
          this.preloadedHeroAssets[heroKey] = true
          return
        }
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = 'image'
        link.href = href
        link.fetchPriority = 'high'
        document.head.appendChild(link)
        this.preloadedHeroAssets[heroKey] = true
      } catch (error) {
        const errorMessage = `Failed to preload asset for hero section "${heroKey}": ${
          error?.message || error
        }`
        this.$store.dispatch('app/setError', errorMessage)
      }
    },
  },
  created() {
    this.updateHeroSection()
    this.$emit('hubspot-hero-visibility-change', { state: this.hubspotHeroState })
    this.$watch(
      () => this.watchVariables,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          this.updateHeroSection()
        }
      },
      { deep: true }
    )
  },
  beforeUnmount() {
    this.heroSectionToShow = ''
    this.$emit('hero-section-to-show', this.heroSectionToShow)
  },
}
</script>

<style lang="scss" scoped>
.hero-section {
  display: flex;
  justify-content: center;
  align-items: center;
  &__hubspot-container {
    @media screen and (max-width: 1100px) {
      padding: 10px;
    }
    @media screen and (max-width: 880px) {
      padding: 60px 10px 10px 10px;
    }
  }
}
</style>
